
import React, { useState, useCallback } from 'react';
import { Users, Plus, UserPlus, Trash2, ShieldCheck, X, Check, ArrowRightLeft, ArrowRight } from 'lucide-react';
import { Team, User, UserRole } from './types';
import { Card, Badge } from './SharedComponents';

interface TeamsPageProps {
  teams: Team[];
  users: User[];
  user: User; // Adicionado para identificar a empresa logada
  onUpdateTeams: (newTeams: any) => void;
  onUpdateUsers: (newUsers: User[]) => void;
  addNotification: (title: string, message: string, type: 'info' | 'warning' | 'error' | 'success', orderId?: string) => void;
}

export const TeamsPage = ({ teams, users, user, onUpdateTeams, onUpdateUsers, addNotification }: TeamsPageProps) => {
  const [isNewTeamModalOpen, setIsNewTeamModalOpen] = useState(false);
  const [addingMemberTo, setAddingMemberTo] = useState<string | null>(null);
  const [transferringMember, setTransferringMember] = useState<{userId: string, fromTeamId: string} | null>(null);
  const [newTeamName, setNewTeamName] = useState('');
  
  const [teamToDelete, setTeamToDelete] = useState<string | null>(null);

  const montadores = users.filter(u => u.role === UserRole.MONTAGEM);

  const handleCreateTeam = useCallback(() => {
    if (!newTeamName) return;
    const newTeam: Team = {
      id: `t${Date.now()}`,
      name: newTeamName,
      leaderId: montadores[0]?.id || '',
      members: [],
      company_id: user.companyId // VÍNCULO CRÍTICO: Agora a equipe nasce na empresa certa
    };
    
    // Usamos função de atualização para não perder dados de outras empresas
    onUpdateTeams((prev: Team[]) => [...prev, newTeam]);
    
    setNewTeamName('');
    setIsNewTeamModalOpen(false);
    addNotification('Equipe Criada', `A ${newTeam.name} já está disponível para despacho.`, 'success');
  }, [newTeamName, montadores, user.companyId, onUpdateTeams, addNotification]);

  const handleRemoveMember = (teamId: string, memberId: string) => {
    onUpdateTeams((prev: Team[]) => prev.map(t => {
      if (t.id === teamId) {
        return { ...t, members: t.members.filter(m => m !== memberId) };
      }
      return t;
    }));
    
    const updatedUsers = users.map(u => {
      if (u.id === memberId) return { ...u, teamId: undefined };
      return u;
    });

    onUpdateUsers(updatedUsers);
    addNotification('Membro Removido', 'Colaborador liberado para outras equipes.', 'info');
  };

  const handleAddMember = (targetTeamId: string, memberId: string) => {
    const userToMove = users.find(u => u.id === memberId);
    const oldTeamId = userToMove?.teamId;

    onUpdateTeams((prev: Team[]) => prev.map(t => {
      if (oldTeamId && t.id === oldTeamId) {
        return { ...t, members: t.members.filter(m => m !== memberId) };
      }
      if (t.id === targetTeamId) {
        return { ...t, members: [...t.members, memberId] };
      }
      return t;
    }));

    const updatedUsers = users.map(u => {
      if (u.id === memberId) return { ...u, teamId: targetTeamId };
      return u;
    });

    onUpdateUsers(updatedUsers);
    setAddingMemberTo(null);
    setTransferringMember(null);

    const targetTeamName = teams.find(t => t.id === targetTeamId)?.name;
    if (oldTeamId) {
      const oldTeamName = teams.find(t => t.id === oldTeamId)?.name;
      addNotification('Transferência Realizada', `${userToMove?.name} movido da ${oldTeamName} para ${targetTeamName}.`, 'success');
    } else {
      addNotification('Equipe Atualizada', `${userToMove?.name} integrado à ${targetTeamName}.`, 'success');
    }
  };

  const handleExecuteDelete = (teamId: string) => {
    const teamToRemove = teams.find(t => t.id === teamId);
    if (!teamToRemove) return;

    onUpdateTeams((prev: Team[]) => prev.filter(t => t.id !== teamId));
    
    const updatedUsers = users.map(u => {
      if (u.teamId === teamId) return { ...u, teamId: undefined };
      return u;
    });
    onUpdateUsers(updatedUsers);
    
    setTeamToDelete(null);
    addNotification('Equipe Excluída', `Equipe ${teamToRemove.name} removida. Funcionários liberados.`, 'warning');
  };

  return (
    <div className="w-full space-y-8 animate-in fade-in duration-500 text-secondary p-4 md:p-8 lg:p-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 px-2">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-secondary uppercase tracking-tighter italic leading-none">Equipes de Campo</h1>
          <p className="text-gray-400 text-xs md:text-sm font-medium italic">Gerenciamento dinâmico de recursos humanos externos.</p>
        </div>
        <button 
          onClick={() => setIsNewTeamModalOpen(true)}
          className="w-full sm:w-auto bg-primary text-white px-8 py-4 rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 transition-all flex items-center justify-center gap-2 active:scale-95"
        >
          <Plus size={18} strokeWidth={3} /> Criar Equipe
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
        {teams.map((team: Team) => (
          <Card key={team.id} className="p-6 md:p-8 space-y-6 group border-2 hover:border-primary/40 transition-all relative bg-white overflow-visible flex flex-col h-full rounded-[40px] shadow-sm hover:shadow-xl">
            
            <div className="absolute top-5 right-5 flex items-center gap-2 z-50">
               {teamToDelete === team.id ? (
                 <div className="flex items-center gap-1 animate-in slide-in-from-right-2 duration-300">
                    <button 
                      onClick={() => handleExecuteDelete(team.id)}
                      className="bg-red-500 text-white px-3 py-2 rounded-xl text-[8px] font-black uppercase tracking-widest hover:bg-red-600 shadow-lg shadow-red-100 flex items-center gap-1.5"
                    >
                       <Check size={12} strokeWidth={4} /> Confirmar
                    </button>
                    <button 
                      onClick={() => setTeamToDelete(null)}
                      className="bg-gray-100 text-gray-400 p-2 rounded-xl hover:bg-gray-200"
                    >
                       <X size={14} />
                    </button>
                 </div>
               ) : (
                 <button 
                  onClick={(e) => { e.stopPropagation(); setTeamToDelete(team.id); }}
                  className="p-2.5 bg-gray-50 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all shadow-sm opacity-60 group-hover:opacity-100"
                  title="Excluir Equipe"
                >
                  <Trash2 size={16} />
                </button>
               )}
            </div>

            <div className="flex justify-between items-start pr-10">
              <div className="p-3 bg-secondary text-white rounded-[18px] shadow-lg">
                <Users size={22} />
              </div>
              <Badge className="bg-primary/10 text-primary border-none px-3 py-1 font-black text-[9px] uppercase tracking-widest">{team.members.length} MEMBROS</Badge>
            </div>
            
            <div className="flex-1">
              <h3 className="text-xl font-black uppercase tracking-tight text-secondary leading-tight line-clamp-2">{team.name}</h3>
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest mt-2 flex items-center gap-1.5 italic">
                <ShieldCheck size={12} className="text-green-500" /> Líder: {users.find(u => u.id === team.leaderId)?.name || 'Nenhum'}
              </p>
            </div>

            <div className="space-y-3 pt-6 border-t border-gray-50 mt-auto">
              {team.members.length > 0 ? team.members.map(memberId => {
                const member = users.find(u => u.id === memberId);
                return (
                  <div key={memberId} className="flex items-center justify-between p-3 bg-gray-50/50 rounded-2xl group/member hover:bg-white transition-all border border-transparent hover:border-gray-100 shadow-inner hover:shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-white border border-gray-100 flex items-center justify-center text-[10px] font-black shadow-sm italic text-primary">
                        {member?.name.charAt(0)}
                      </div>
                      <span className="font-bold text-xs text-secondary uppercase tracking-tight">{member?.name}</span>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover/member:opacity-100 transition-opacity">
                      <button 
                        onClick={() => setTransferringMember({ userId: memberId, fromTeamId: team.id })}
                        className="text-gray-300 hover:text-primary p-2 transition-all hover:bg-primary/5 rounded-lg"
                        title="Mudar Equipe"
                      >
                        <ArrowRightLeft size={14} />
                      </button>
                      <button 
                        onClick={() => handleRemoveMember(team.id, memberId)}
                        className="text-gray-300 hover:text-red-500 p-2 transition-all hover:bg-red-50 rounded-lg"
                        title="Remover"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              }) : (
                <p className="text-center py-6 text-[10px] text-gray-300 font-black uppercase tracking-widest italic border-2 border-dashed border-gray-100 rounded-3xl">Equipe aguardando escala</p>
              )}
              
              <button 
                onClick={() => setAddingMemberTo(team.id)}
                className="w-full py-4 border-2 border-dashed border-gray-200 rounded-[24px] text-[10px] font-black uppercase text-gray-400 hover:border-primary/50 hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2 group/add active:scale-95"
              >
                <UserPlus size={16} className="group-hover/add:scale-110 transition-transform" /> Escalar Colaborador
              </button>
            </div>
          </Card>
        ))}

        {teams.length === 0 && (
          <div className="col-span-full py-32 flex flex-col items-center justify-center opacity-30 text-center gap-6">
             <Users size={80} strokeWidth={1} />
             <p className="text-sm font-black uppercase tracking-[0.4em]">Nenhuma equipe operacional configurada</p>
          </div>
        )}
      </div>

      {/* Modal: Criar Equipe - COMPACTADO */}
      {isNewTeamModalOpen && (
        <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[1000] flex items-center justify-center p-6">
          <div className="bg-white rounded-[40px] w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
             <div className="p-6 bg-primary text-white flex justify-between items-center shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl shadow-inner"><Plus size={20} strokeWidth={3} /></div>
                  <h2 className="text-lg font-black uppercase tracking-tight italic leading-none">Nova Equipe</h2>
                </div>
                <button onClick={() => setIsNewTeamModalOpen(false)} className="p-2 hover:bg-white/10 rounded-xl transition-all active:scale-90"><X size={24} /></button>
             </div>
             <div className="p-6 space-y-6">
                <div className="space-y-1.5">
                   <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest ml-1">Nome da Equipe</label>
                   <input 
                    autoFocus
                    className="w-full bg-gray-50 border border-gray-200 p-4 rounded-2xl outline-none font-black text-secondary shadow-inner uppercase text-sm"
                    placeholder="Ex: Squad Beta"
                    value={newTeamName}
                    onChange={e => setNewTeamName(e.target.value)}
                   />
                </div>
                <button 
                  disabled={!newTeamName}
                  onClick={handleCreateTeam}
                  className="w-full py-4 bg-primary text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-primary/20 hover:brightness-110 disabled:opacity-50 transition-all active:scale-95"
                >
                   Finalizar e Ativar
                </button>
             </div>
          </div>
        </div>
      )}

      {/* Modal: Transferir Membro de Equipe - COMPACTADO */}
      {transferringMember && (
        <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[1100] flex items-center justify-center p-6">
           <div className="bg-white rounded-[40px] w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95">
              <div className="p-6 bg-secondary text-white flex justify-between items-center">
                 <div>
                    <h2 className="text-lg font-black uppercase tracking-tight italic leading-none">Mudar Equipe</h2>
                    <p className="text-[8px] font-bold text-white/40 uppercase tracking-widest mt-1">
                       Mover {users.find(u => u.id === transferringMember.userId)?.name}
                    </p>
                 </div>
                 <button onClick={() => setTransferringMember(null)} className="p-2 hover:bg-white/10 rounded-xl"><X size={20} /></button>
              </div>
              <div className="p-5 space-y-2">
                 <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2 px-1 italic">Destino:</p>
                 <div className="max-h-[300px] overflow-y-auto scrollbar-hide">
                    {teams.filter(t => t.id !== transferringMember.fromTeamId).map(t => (
                        <button 
                          key={t.id}
                          onClick={() => handleAddMember(t.id, transferringMember.userId)}
                          className="w-full p-4 rounded-2xl bg-gray-50 hover:bg-primary hover:text-white transition-all border border-gray-100 flex items-center justify-between group active:scale-[0.98] mb-1.5"
                        >
                          <span className="font-black uppercase tracking-tight text-[10px]">{t.name}</span>
                          <ArrowRight size={14} className="text-gray-300 group-hover:text-white" />
                        </button>
                    ))}
                 </div>
                 {teams.filter(t => t.id !== transferringMember.fromTeamId).length === 0 && (
                   <div className="py-6 text-center opacity-30 italic text-[10px] uppercase font-bold">Nenhuma outra equipe disponível</div>
                 )}
              </div>
              <div className="p-5 pt-1 border-t border-gray-50 bg-gray-50/30">
                 <button 
                  onClick={() => handleRemoveMember(transferringMember.fromTeamId, transferringMember.userId)}
                  className="w-full py-3 text-[8px] font-black uppercase text-red-500 hover:bg-red-50 rounded-xl transition-all"
                 >
                    Remover e deixar em espera
                 </button>
              </div>
           </div>
        </div>
      )}

      {/* Modal: Adicionar Membro - COMPACTADO */}
      {addingMemberTo && (
        <div className="fixed inset-0 bg-secondary/80 backdrop-blur-sm z-[1000] flex items-center justify-center p-6">
          <div className="bg-white rounded-[40px] w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
             <div className="p-6 bg-secondary text-white flex justify-between items-center">
                <div>
                   <h2 className="text-lg font-black uppercase tracking-tight italic leading-none">Escalar Membro</h2>
                   <p className="text-[8px] font-bold text-white/40 uppercase tracking-widest mt-1">SQUAD: {teams.find(t => t.id === addingMemberTo)?.name}</p>
                </div>
                <button onClick={() => setAddingMemberTo(null)} className="p-2 hover:bg-white/10 rounded-xl"><X size={20} /></button>
             </div>
             
             <div className="max-h-[400px] overflow-y-auto p-4 space-y-1.5 scrollbar-hide bg-[#fcfdfe]">
                {montadores.filter(u => !u.teamId).length > 0 ? (
                  montadores
                    .filter(u => !u.teamId)
                    .map(u => (
                      <button 
                        key={u.id}
                        onClick={() => handleAddMember(addingMemberTo, u.id)}
                        className="w-full flex items-center justify-between p-3.5 rounded-2xl hover:bg-primary/5 group transition-all border border-transparent hover:border-primary/10 bg-white shadow-sm"
                      >
                         <div className="flex items-center gap-3 text-left">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black group-hover:bg-primary group-hover:text-white transition-all bg-green-50 text-green-600 shadow-inner italic text-xs">
                              {u.name.charAt(0)}
                            </div>
                            <div>
                               <p className="font-black text-secondary uppercase tracking-tight text-xs">{u.name}</p>
                               <p className="text-[8px] text-green-500 font-bold uppercase tracking-widest flex items-center gap-1 italic">
                                  <Check size={8} strokeWidth={4} /> Disponível
                               </p>
                            </div>
                         </div>
                         <div className="p-1.5 bg-gray-50 rounded-lg group-hover:bg-primary group-hover:text-white transition-all">
                           <Plus size={14} />
                         </div>
                      </button>
                    ))
                ) : (
                  <div className="p-10 text-center space-y-4">
                     <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto shadow-inner text-gray-200">
                        <Users size={32} strokeWidth={1} />
                     </div>
                     <p className="text-[10px] text-primary font-bold uppercase italic opacity-60">
                        Nenhum colaborador disponível para escala.
                     </p>
                  </div>
                )}
             </div>
          </div>
        </div>
      )}
    </div>
  );
};
